import { GRAF_COLUMNS } from '../../hooks/useGrafData'

function formatValue(value) {
  if (typeof value === 'number') return value % 1 === 0 ? String(value) : value.toFixed(1)
  return String(value)
}

function formatDateStr(dateStr) {
  const [y, m, d] = dateStr.split('-')
  return `${d}.${m}.${y}`
}

const cellBorder = 'border-b border-r border-gray-200 last:border-r-0'
const thBase = `${cellBorder} px-2 py-1 text-xs font-normal text-center bg-gray-50 whitespace-nowrap`
const th1 = `${thBase} font-semibold text-gray-600 border-t-2 border-t-gray-400`
const th3 = `${thBase} text-gray-400`

export function GrafDataGrid({ dataMap, labelMap, dayMap, orientation = 'normal' }) {
  if (orientation === 'transposed') {
    return <GrafDataGridTransposed dataMap={dataMap} labelMap={labelMap} dayMap={dayMap} />
  }

  const times = Array.from(labelMap.keys()).sort()

  // Zjistí kde se mění den (pro oddělovač)
  function isDayBorder(time, prevTime) {
    if (!prevTime) return false
    return dayMap.get(time)?.dateStr !== dayMap.get(prevTime)?.dateStr
  }

  // Hodinový oddělovač (stejný jako v MinGrid)
  function isHourBorder(label) {
    return label.endsWith(':00')
  }

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-l border-l-gray-400 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <thead className="sticky top-0 z-20">
            {/* Řádek 1 — datum + skupinová hlavička */}
            <tr>
              <th rowSpan={2} className="sticky left-0 z-10 bg-gray-50 border-t-2 border-t-gray-400 border-b border-b-gray-200 border-r border-r-gray-400 px-2 py-1 text-gray-500 font-normal whitespace-nowrap">
                Čas
              </th>
              {GRAF_COLUMNS.map((col, ci) => (
                <th
                  key={col.tsId}
                  className={`${th1} ${ci === 0 ? '' : 'border-l border-l-gray-300'}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
            {/* Řádek 2 — jednotky */}
            <tr>
              {GRAF_COLUMNS.map((col, ci) => (
                <th
                  key={`${col.tsId}-u`}
                  className={`${th3} ${ci === 0 ? '' : 'border-l border-l-gray-300'}`}
                >
                  {col.unit}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {times.map((time, idx) => {
              const entry = dataMap.get(time)
              const label = labelMap.get(time) ?? time
              const info = dayMap.get(time)
              const prevTime = idx > 0 ? times[idx - 1] : null
              const dayBorder = isDayBorder(time, prevTime)
              const hourBorder = isHourBorder(label)

              // Den oddělovač: 2px stejná barva jako oddělovač hodin (border-t-gray-400)
              const topBorder = dayBorder
                ? 'border-t-2 border-t-gray-400'
                : hourBorder
                  ? 'border-t border-t-gray-400'
                  : ''

              return (
                <tr key={time} className="group">
                  <td className={`sticky left-0 z-10 bg-gray-50 border-b border-b-gray-200 border-r border-r-gray-400 group-last:border-b-0 px-2 py-0.5 text-gray-500 text-right whitespace-nowrap font-mono ${topBorder}`}>
                    {dayBorder && info?.dateStr ? (
                      <span className="flex flex-col items-end gap-0">
                        <span className="text-gray-400 text-xs">{formatDateStr(info.dateStr)}</span>
                        <span>{label}</span>
                      </span>
                    ) : label}
                  </td>
                  {GRAF_COLUMNS.map((col, ci) => {
                    const value = entry?.[col.tsId] ?? null
                    return (
                      <td
                        key={col.tsId}
                        className={`${cellBorder} group-last:border-b-0 px-1 py-0.5 text-right tabular-nums whitespace-nowrap bg-white ${ci > 0 ? 'border-l border-l-gray-300' : ''} ${value == null ? 'text-gray-300' : ''} ${topBorder}`}
                      >
                        {value != null ? formatValue(value) : '—'}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
            {times.length === 0 && (
              <tr>
                <td colSpan={GRAF_COLUMNS.length + 1} className="px-4 py-6 text-center text-gray-400 border-b border-r border-gray-200 border-b-0">
                  Žádná data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Transponovaná varianta ─────────────────────────────────────────────────────
// řádky = veličiny, sloupce = časové kroky. Vzhled sjednocený s YlyStatsGrid (netransponovaný):
// 1 sticky sloupec "Veličina" (min-w 16rem, popis + jednotka "(°C)"), tenké čáry gray-200,
// gray-400 jen kolem hlavičky a sticky sloupce.
const tCb = 'border-b border-r border-gray-200 px-1 py-0.5 text-xs tabular-nums whitespace-nowrap'
const tThBase = 'border-b border-b-gray-200 border-r border-r-gray-200 px-1 py-1 text-xs font-normal text-center bg-gray-50'

function GrafDataGridTransposed({ dataMap, labelMap, dayMap }) {
  const times = Array.from(labelMap.keys()).sort()

  const isHourBorder = (label) => label.endsWith(':00')

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <thead className="sticky top-0 z-20">
            <tr>
              <th className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l-2 border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left text-gray-600 font-semibold min-w-[16rem] whitespace-nowrap">
                Veličina
              </th>
              {times.map((time, idx) => {
                const label = labelMap.get(time) ?? time
                const info = dayMap.get(time)
                const prevTime = idx > 0 ? times[idx - 1] : null
                const dayBorder = !!prevTime && dayMap.get(time)?.dateStr !== dayMap.get(prevTime)?.dateStr
                const hourBorder = isHourBorder(label)
                const leftBorder = dayBorder ? 'border-l-2 border-l-gray-400' : hourBorder ? 'border-l border-l-gray-400' : ''
                return (
                  <th
                    key={time}
                    className={`${tThBase} border-t-2 border-t-gray-400 border-b-gray-400 text-gray-500 ${leftBorder}`}
                  >
                    {dayBorder && info?.dateStr ? (
                      <span className="flex flex-col items-center gap-0">
                        <span className="text-gray-400 text-[10px]">{formatDateStr(info.dateStr)}</span>
                        <span>{label}</span>
                      </span>
                    ) : label}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {GRAF_COLUMNS.map((col) => (
              <tr key={col.tsId} className="group hover:bg-slate-50">
                <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-l-2 border-l-gray-400 border-r border-r-gray-400 group-last:border-b-0 px-3 py-0.5 text-left text-gray-700 whitespace-nowrap">
                  {col.label}
                  {col.unit && <span className="text-gray-400 ml-1.5 text-[10px]">({col.unit})</span>}
                </td>
                {times.map((time, idx) => {
                  const label = labelMap.get(time) ?? time
                  const prevTime = idx > 0 ? times[idx - 1] : null
                  const dayBorder = !!prevTime && dayMap.get(time)?.dateStr !== dayMap.get(prevTime)?.dateStr
                  const hourBorder = isHourBorder(label)
                  const leftBorder = dayBorder ? 'border-l-2 border-l-gray-400' : hourBorder ? 'border-l border-l-gray-400' : ''
                  const value = dataMap.get(time)?.[col.tsId] ?? null
                  return (
                    <td
                      key={time}
                      className={`${tCb} text-right bg-white group-hover:bg-slate-50 group-last:border-b-0 ${leftBorder} ${value == null ? 'text-gray-300' : 'text-gray-700'}`}
                    >
                      {value != null ? formatValue(value) : '—'}
                    </td>
                  )
                })}
              </tr>
            ))}
            {times.length === 0 && (
              <tr>
                <td colSpan={1 + GRAF_COLUMNS.length} className="px-4 py-6 text-center text-gray-400">
                  Žádná data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
